
import React from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { allEntries, localizeEntry } from "@/utils/changelog-i18n";

interface FooterProps {
  children?: React.ReactNode;
  onFeedbackOpen?: () => void;
}

const Footer: React.FC<FooterProps> = ({ children, onFeedbackOpen }) => {
  const currentYear = new Date().getFullYear();
  const { t, i18n } = useTranslation("nav");
  const dateLocale = i18n.language?.startsWith("ja") ? "ja-JP" : "en-US";
  const latestEntry = localizeEntry(allEntries[0], i18n.language);

  return (
    <footer className="bg-card flex w-full items-center gap-6 overflow-hidden flex-wrap py-4 px-8 border-border border-t max-md:max-w-full max-md:px-5">
      <div className="self-stretch flex min-h-8 items-center gap-2 flex-1 shrink basis-[0%] max-md:max-w-full">
        <Link
          to="/changelog"
          className="text-white hover:underline mr-4"
        >
          {t("changelog")}
        </Link>
        <Link
          to="/about"
          className="text-white hover:underline mr-4"
        >
          {t("about")}
        </Link>
        {children}
      </div>
      
      <div className="flex gap-4 items-center">
        <a
          href="https://ko-fi.com/inkandinsanity"
          target="_blank"
          rel="noopener noreferrer"
          className="text-white hover:underline"
        >
          {t("support")}
        </a>
        <button
          onClick={onFeedbackOpen}
          className="text-white hover:underline"
        >
          {t("feedback")}
        </button>
      </div>
      
      <div className="text-neutral-400 text-sm text-center w-full mt-4">
        <p className="text-neutral-500 text-xs mb-1 flex items-center justify-center gap-2 flex-wrap">
          <span>
            {t("version", {
              version: __APP_VERSION__,
              date: new Date(__BUILD_DATE__).toLocaleDateString(dateLocale, {
                year: "numeric",
                month: "short",
                day: "numeric",
              }),
            })}
          </span>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-brand-green hover:text-brand-cream transition-colors"
                aria-label={t("whatsNewAria")}
              >
                <Sparkles className="h-3 w-3" />
                <span>{t("whatsNew")}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="center"
              side="top"
              className="w-80 bg-card border-border text-left"
            >
              <div className="space-y-2">
                <span className="text-xs uppercase tracking-wide text-brand-green font-semibold">
                  {latestEntry.category}
                </span>
                <h4 className="text-sm font-semibold text-foreground font-sans">
                  {latestEntry.title}
                </h4>
                <p className="text-xs text-neutral-300 leading-relaxed">
                  {latestEntry.description}
                </p>
                <Link
                  to={`/changelog#${latestEntry.slug}`}
                  className="inline-block text-xs text-brand-green hover:underline pt-1"
                >
                  {t("readFullChangelog")} →
                </Link>
              </div>
            </PopoverContent>
          </Popover>
        </p>
        <p>{t("copyright", { year: currentYear })}</p>
        <p>{t("trademark")}</p>
      </div>
    </footer>
  );
};

export default Footer;
